import type { RenameScoutConversationMutation } from "generated-graphql/graphql";

import { useMutation, type ApolloCache, type FetchResult } from "@apollo/client";
import { gql } from "generated-graphql";
import { useDebounce } from "hooks/useDebounce";
import { useCallback, useEffect, useRef, useState } from "react";

import { isScoutOptimisticConversationId } from "./hooks/scoutConversationIds";
import { scoutConversationStore } from "./hooks/scoutConversationStore";

export const MAX_SCOUT_CONVERSATION_TITLE_LENGTH = 80;
export const SCOUT_CONVERSATION_TITLE_SAVE_DEBOUNCE_MS = 500;

const RENAME_SCOUT_CONVERSATION = gql(`
  mutation RenameScoutConversation($input: RenameScoutConversationInput!) {
    renameScoutConversation(input: $input) {
      conversationId
      title
    }
  }
`);

function updateScoutConversationTitleInCache(
  cache: ApolloCache<unknown>,
  conversationId: string,
  title: string
) {
  cache.modify({
    id: cache.identify({
      __typename: "ScoutConversationListItem",
      id: conversationId,
    }),
    fields: {
      title() {
        return title;
      },
    },
  });
}

async function persistConversationTitleRename(input: {
  renameConversation: (options: {
    variables: {
      input: { tenantId: string; conversationId: string; title: string };
    };
    update: (
      cache: ApolloCache<unknown>,
      result: Omit<FetchResult<RenameScoutConversationMutation>, "context">
    ) => void;
  }) => Promise<FetchResult<RenameScoutConversationMutation>>;
  tenantId: string;
  conversationId: string;
  title: string;
}): Promise<string | undefined> {
  const result = await input.renameConversation({
    variables: {
      input: {
        tenantId: input.tenantId,
        conversationId: input.conversationId,
        title: input.title,
      },
    },
    update(cache, { data }) {
      const renamed = data?.renameScoutConversation;
      if (renamed) {
        updateScoutConversationTitleInCache(cache, renamed.conversationId, renamed.title);
      }
    },
  });
  return result.data?.renameScoutConversation?.title;
}

type UseScoutConversationTitleRenameInput = {
  conversationId?: string;
  tenantId?: string;
  showTitle: boolean;
  title: string | undefined;
};

export function useScoutConversationTitleRename({
  conversationId,
  tenantId,
  showTitle,
  title,
}: UseScoutConversationTitleRenameInput) {
  const canRename =
    Boolean(conversationId && tenantId && showTitle) &&
    !isScoutOptimisticConversationId(conversationId);

  const savedTitle = title ?? "";
  const [draftTitle, setDraftTitle] = useState(savedTitle);
  const [lastSavedTitle, setLastSavedTitle] = useState(savedTitle);
  const [syncedConversationId, setSyncedConversationId] = useState(conversationId);
  const [isFocused, setIsFocused] = useState(false);
  const [prevExternalTitle, setPrevExternalTitle] = useState(savedTitle);
  const saveGenerationRef = useRef(0);
  const inFlightSavesRef = useRef(0);
  const [isSaving, setIsSaving] = useState(false);
  const [renameConversation] = useMutation(RENAME_SCOUT_CONVERSATION);
  const [debouncedTitle, isDebounceWaiting] = useDebounce(
    draftTitle,
    SCOUT_CONVERSATION_TITLE_SAVE_DEBOUNCE_MS
  );

  if (conversationId !== syncedConversationId) {
    setSyncedConversationId(conversationId);
    setDraftTitle(savedTitle);
    setLastSavedTitle(savedTitle);
    setPrevExternalTitle(savedTitle);
    setIsFocused(false);
  }

  // Adopt external title changes when the local draft isn't dirty.
  if (!isFocused && savedTitle !== prevExternalTitle && draftTitle === lastSavedTitle) {
    setPrevExternalTitle(savedTitle);
    setDraftTitle(savedTitle);
    setLastSavedTitle(savedTitle);
  }

  useEffect(() => {
    if (!canRename || !conversationId || !tenantId) {
      return;
    }
    // Ignore a stale debounce tick that no longer matches what the user typed.
    if (debouncedTitle !== draftTitle) {
      return;
    }

    const nextTitle = debouncedTitle.trim();
    if (nextTitle.length === 0 || nextTitle === lastSavedTitle) {
      return;
    }

    const generation = ++saveGenerationRef.current;
    inFlightSavesRef.current += 1;
    setIsSaving(true);
    void (async () => {
      try {
        const saved = await persistConversationTitleRename({
          renameConversation,
          tenantId,
          conversationId,
          title: nextTitle,
        });
        if (generation === saveGenerationRef.current && saved) {
          setLastSavedTitle(saved);
          setPrevExternalTitle(saved);
          scoutConversationStore.setConversationTitle({ conversationId, title: saved });
        }
      } catch {
        // Leave the draft as-is so the next edit can retry.
      } finally {
        inFlightSavesRef.current = Math.max(0, inFlightSavesRef.current - 1);
        if (inFlightSavesRef.current === 0) {
          setIsSaving(false);
        }
      }
    })();
  }, [
    canRename,
    conversationId,
    debouncedTitle,
    draftTitle,
    lastSavedTitle,
    renameConversation,
    tenantId,
  ]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    if (draftTitle.trim().length === 0) {
      setDraftTitle(lastSavedTitle);
    }
  }, [draftTitle, lastSavedTitle]);

  const trimmedDraft = draftTitle.trim();
  const hasPendingTitleChange = trimmedDraft.length > 0 && trimmedDraft !== lastSavedTitle;
  const showSaveSpinner = isSaving || (isDebounceWaiting && hasPendingTitleChange);
  // Block leaving while a rename is in flight or the draft still differs from
  // what was last saved (including the debounce window before the mutation).
  const hasUnsavedTitleChange = hasPendingTitleChange || isSaving;

  return {
    canRename,
    draftTitle,
    setDraftTitle,
    showSaveSpinner,
    hasUnsavedTitleChange,
    setIsFocused,
    handleBlur,
  };
}
